import * as React from 'react';

import { Radio } from '@/components/ui/radio';
import { cn } from '@/lib/utils';

type RadioGroupContextValue = {
  name: string;
  value: string | null;
  onValueChange: (value: string) => void;
};

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

function useRadioGroup(): RadioGroupContextValue {
  const context = React.useContext(RadioGroupContext);
  if (!context) {
    throw new Error('RadioGroupItem must be used inside <RadioGroup>.');
  }
  return context;
}

type RadioGroupProps = {
  name: string;
  value: string | null;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
  'aria-label'?: string;
};

export function RadioGroup({
  name,
  value,
  onValueChange,
  children,
  className,
  'aria-label': ariaLabel,
}: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ name, value, onValueChange }}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className={cn('flex flex-col gap-3', className)}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

type RadioGroupItemProps = {
  value: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  className?: string;
};

export function RadioGroupItem({
  value,
  title,
  description,
  disabled,
  className,
}: RadioGroupItemProps) {
  const group = useRadioGroup();
  const isSelected = group.value === value;
  const inputId = `${group.name}-${value}`;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'relative flex items-start gap-2 rounded-[14px] border p-4 transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:border-muted-foreground/60',
        isSelected && !disabled ? 'border-muted-foreground' : 'border-border',
        className,
      )}
    >
      <Radio
        id={inputId}
        name={group.name}
        value={value}
        checked={isSelected}
        disabled={disabled}
        onChange={() => group.onValueChange(value)}
        className="mt-0.5"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-px text-left">
        <div className="text-base font-medium leading-4">{title}</div>
        {description ? (
          <div className="text-sm leading-5 text-muted-foreground">{description}</div>
        ) : null}
      </div>
    </label>
  );
}
